import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaArrowRight, 
  FaMobile, 
  FaCheck, 
  FaApple,
  FaAndroid,
  FaReact,
  FaRocket,
  FaShieldAlt,
  FaClock,
  FaUsers,
  FaStar,
  FaQuoteLeft,
  FaDownload,
  FaGlobe,
  FaCode,
  FaChartLine,
  FaUsersCog
} from 'react-icons/fa';
import { SiFlutter, SiFirebase, SiTypescript, SiKotlin, SiSwift, SiNodedotjs, SiIonic } from 'react-icons/si';
import { serviceSeoData, seoImage } from '../../config/pageSeoConfig';
import './MobileApp.scss';

const MobileApps: React.FC = () => {
  // SEO constants from config
  const seoTitle = serviceSeoData.mobileApps.title;
  const seoDescription = serviceSeoData.mobileApps.description;
  const seoKeywords = serviceSeoData.mobileApps.keywords;
  const seoUrl = serviceSeoData.mobileApps.url;
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Mobile App Development Services",
    "description": seoDescription,
    "provider": {
      "@type": "Organization",
      "name": "App Developer Pro",
      "url": "https://appdeveloperpro.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://appdeveloperpro.com/images/team-at-work-app-developer-pro.jpg"
      }
    },
    "areaServed": "Worldwide",
    "serviceType": "Mobile App Development, iOS App Development, Android App Development, React Native, Flutter, Cross-Platform Apps, App Store Launch, Cloud Integration",
    "url": seoUrl,
    "image": seoImage
  };
  const features = [
    {
      icon: FaApple,
      title: 'Native iOS Development',
      description: 'High-performance iOS apps built with Swift and optimized for Apple devices'
    },
    {
      icon: FaAndroid,
      title: 'Native Android Development',
      description: 'Robust Android applications using Kotlin/Java for Google Play Store'
    },
    {
      icon: SiFlutter,
      title: 'Cross-Platform Solutions',
      description: 'Cost-effective apps for both iOS and Android using React Native or Flutter'
    },
    {
      icon: FaGlobe,
      title: 'Progressive Web Apps',
      description: 'Web-based apps that work like native apps with offline capabilities'
    },
    {
      icon: SiFirebase,
      title: 'Cloud Integration',
      description: 'Seamless backend integration with real-time data synchronization'
    },
    {
      icon: FaShieldAlt,
      title: 'Security & Privacy',
      description: 'Advanced security measures to protect user data and transactions'
    }
  ];

  const platforms = [
    { icon: FaApple, name: 'iOS', color: '#000000', description: 'Native iOS apps for iPhone and iPad' },
    { icon: FaAndroid, name: 'Android', color: '#3DDC84', description: 'Native Android apps for all devices' },
    { icon: FaReact, name: 'React Native', color: '#61DAFB', description: 'Cross-platform mobile development' },
    { icon: SiFlutter, name: 'Flutter', color: '#02569B', description: 'Google\'s UI toolkit for mobile' },
    { icon: SiFirebase, name: 'Firebase', color: '#FFCA28', description: 'Backend-as-a-Service platform' },
    { icon: SiTypescript, name: 'TypeScript', color: '#3178C6', description: 'Type-safe mobile development' },
    { icon: SiKotlin, name: 'Kotlin', color: '#7F52FF', description: 'Modern language for Android development' },
    { icon: SiSwift, name: 'Swift', color: '#FA7343', description: 'Apple\'s programming language for iOS' },
    { icon: SiNodedotjs, name: 'Node.js', color: '#339933', description: 'Backend services for mobile apps' },
    { icon: SiIonic, name: 'Ionic', color: '#3880FF', description: 'Hybrid mobile app development' }
  ];

  const benefits = [
    {
      icon: FaRocket,
      title: 'Fast Performance',
      description: 'Optimized apps with smooth animations and quick load times'
    },
    {
      icon: FaDownload,
      title: 'App Store Ready',
      description: 'Complete app store submission and approval assistance'
    },
    {
      icon: FaUsers,
      title: 'User-Centric Design',
      description: 'Intuitive interfaces designed for maximum user engagement'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Concept & Strategy',
      description: 'Define app objectives, target audience, and feature requirements'
    },
    {
      step: '02',
      title: 'UI/UX Design',
      description: 'Create wireframes, prototypes, and stunning user interfaces'
    },
    {
      step: '03',
      title: 'Development',
      description: 'Build your app using cutting-edge technologies and best practices'
    },
    {
      step: '04',
      title: 'Testing & Launch',
      description: 'Comprehensive testing followed by app store deployment'
    }
  ];

  const completedProjects = [
    {
      title: 'Fitness Tracking App',
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&auto=format&fit=crop',
      technologies: [
        { name: 'React Native', icon: FaReact, color: '#61DAFB' },
        { name: 'Firebase', icon: SiFirebase, color: '#FFCA28' },
        { name: 'Node.js', icon: SiNodedotjs, color: '#339933' }
      ],
      description: 'Cross-platform fitness app with real-time workout tracking and social features',
      metrics: {
        users: '150K+',
        rating: '4.8/5',
        downloads: '500K+'
      }
    },
    {
      title: 'E-Commerce Mobile App',
      image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&auto=format&fit=crop',
      technologies: [
        { name: 'Flutter', icon: SiFlutter, color: '#02569B' },
        { name: 'TypeScript', icon: SiTypescript, color: '#3178C6' },
        { name: 'Firebase', icon: SiFirebase, color: '#FFCA28' }
      ],
      description: 'Native shopping experience with payment gateway and order tracking',
      metrics: {
        transactions: '25K/mo',
        rating: '4.7/5',
        revenue: '$2M+'
      }
    },
    {
      title: 'Healthcare Patient Portal',
      image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800&auto=format&fit=crop',
      technologies: [
        { name: 'iOS Swift', icon: FaApple, color: '#000000' },
        { name: 'Android Kotlin', icon: FaAndroid, color: '#3DDC84' },
        { name: 'Node.js', icon: SiNodedotjs, color: '#339933' }
      ],
      description: 'HIPAA-compliant medical app with telemedicine and appointment scheduling',
      metrics: {
        patients: '50K+',
        appointments: '10K/mo',
        rating: '4.9/5'
      }
    }
  ];

  const testimonials = [
    {
      text: "Our mobile app exceeded all expectations. The team delivered a beautiful, high-performance app that our users love.",
      author: "Jennifer Davis",
      company: "FinTech Startup",
      rating: 5
    },
    {
      text: "From concept to app store, the development process was smooth and professional. Highly recommend their mobile app services.",
      author: "David Wilson",
      company: "Retail Chain",
      rating: 5
    }
  ];

  const appTypes = [
    {
      title: 'Business Apps',
      description: 'Enterprise solutions for productivity and workflow management',
      icon: FaCode
    },
    {
      title: 'E-commerce Apps',
      description: 'Shopping apps with payment integration and inventory management',
      icon: FaMobile
    },
    {
      title: 'Social Apps',
      description: 'Community platforms with chat, sharing, and social features',
      icon: FaUsers
    },
    {
      title: 'Healthcare Apps',
      description: 'HIPAA-compliant medical and wellness applications',
      icon: FaShieldAlt
    }
  ];

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content="mobile app development, ios app, android app, react native, flutter, cross-platform app, app store launch, cloud integration, mobile application, 2025" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={seoUrl} />
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:image" content={seoImage} />
        <meta property="og:site_name" content="App Developer Pro" />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoImage} />
        <meta name="twitter:site" content="@appdeveloperpro" />
        {/* Schema.org Markup */}
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>
      <div className="mobile-apps">
      {/* Hero Section */}
      <section className="mobile-apps__hero">
        <div className="mobile-apps__hero-content">
          <motion.div
            className="mobile-apps__hero-text"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="mobile-apps__hero-title">
              Mobile <span className="mobile-apps__hero-highlight">App Development - </span> iOS & Android Apps
            </h1>
            <h2>Your Native App Development Partner🤝</h2> 
            <p className="mobile-apps__hero-subtitle">
              App Developer Pro is here for mobile app development services & builds native iOS, Android, and cross-platform apps using the latest technologies. Create your powerful mobile application that engage users and drive business growth. 
            </p>
            <div className="mobile-apps__hero-buttons">
              <Link to="/contact" className="mobile-apps__hero-btn mobile-apps__hero-btn--primary">
                Start Your App <FaArrowRight />
              </Link>
              <Link to="/portfolio" className="mobile-apps__hero-btn mobile-apps__hero-btn--secondary">
                View App Portfolio
              </Link>
            </div>
          </motion.div>
          <motion.div
            className="mobile-apps__hero-visual"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="mobile-apps__hero-phones">
              <div className="mobile-apps__hero-phone mobile-apps__hero-phone--ios">
                <div className="mobile-apps__hero-phone-frame">
                  <div className="mobile-apps__hero-phone-notch"></div>
                  <div className="mobile-apps__hero-phone-screen">
                    <div className="mobile-apps__hero-phone-header">
                      <div className="mobile-apps__hero-phone-time">9:41</div>
                      <div className="mobile-apps__hero-phone-status">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                    <div className="mobile-apps__hero-phone-content">
                      <div className="mobile-apps__hero-phone-app-icon">
                        <FaMobile />
                      </div>
                      <h3>Your App</h3>
                      <p>Beautiful UI/UX</p>
                      <div className="mobile-apps__hero-phone-buttons">
                        <button className="mobile-apps__hero-phone-btn">Get Started</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mobile-apps__hero-phone mobile-apps__hero-phone--android">
                <div className="mobile-apps__hero-phone-frame">
                  <div className="mobile-apps__hero-phone-screen">
                    <div className="mobile-apps__hero-phone-header">
                      <div className="mobile-apps__hero-phone-time">14:25</div>
                      <div className="mobile-apps__hero-phone-status">
                        <FaAndroid />
                      </div>
                    </div>
                    <div className="mobile-apps__hero-phone-content">
                      <div className="mobile-apps__hero-phone-app-icon mobile-apps__hero-phone-app-icon--android">
                        <FaRocket />
                      </div>
                      <h3>Amazing App</h3>
                      <p>Cross-Platform</p>
                      <div className="mobile-apps__hero-phone-buttons">
                        <button className="mobile-apps__hero-phone-btn mobile-apps__hero-phone-btn--android">Launch</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="mobile-apps__features">
        <div className="mobile-apps__section-header">
          <motion.h2
            className="mobile-apps__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Mobile App Development Services
          </motion.h2>
          <motion.p
            className="mobile-apps__section-subtitle"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
           Delivering high-performance, feature-rich mobile applications tailored to your vision. App Developer Pro is known to create seamless, secure, and scalable apps for iOS, Android, and cross-platform ecosystems—ensuring exceptional user experience, smooth performance, and real business impact across every device.
          </motion.p>
        </div>
        <div className="mobile-apps__features-grid">
          {features.map((feature, index) => {
            const FeatureIcon = feature.icon;
            return (
              <motion.div
                key={feature.title}
                className="mobile-apps__feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="mobile-apps__feature-header">
                  <div className="mobile-apps__feature-icon">
                    <FeatureIcon />
                  </div>
                  <h3 className="mobile-apps__feature-title">{feature.title}</h3>
                </div>
                <p className="mobile-apps__feature-description">{feature.description}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Platforms Section */}
      <section className="mobile-apps__platforms">
        <motion.div
          className="mobile-apps__section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="mobile-apps__section-title">Platforms & Technologies</h2>
          <p className="mobile-apps__section-subtitle">
            We work with the latest mobile development platforms and frameworks
          </p>
        </motion.div>
        <div className="mobile-apps__platforms-grid">
          {platforms.map((platform, index) => {
            const PlatformIcon = platform.icon;
            return (
              <motion.div
                key={platform.name}
                className="mobile-apps__platform-item"
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05, y: -5 }}
              >
                <div 
                  className="mobile-apps__platform-icon"
                  style={{ '--platform-color': platform.color } as React.CSSProperties}
                >
                  <PlatformIcon />
                </div>
                <h3 className="mobile-apps__platform-name">{platform.name}</h3>
                <p className="mobile-apps__platform-description">{platform.description}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* App Types Section */}
      <section className="mobile-apps__app-types">
        <div className="mobile-apps__section-header">
          <motion.h2
            className="mobile-apps__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Types of Apps We Build
          </motion.h2>
        </div>
        <div className="mobile-apps__app-types-grid">
          {appTypes.map((type, index) => {
            const TypeIcon = type.icon;
            return (
              <motion.div
                key={type.title}
                className="mobile-apps__app-type-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="mobile-apps__app-type-icon">
                  <TypeIcon />
                </div>
                <h3 className="mobile-apps__app-type-title">{type.title}</h3>
                <p className="mobile-apps__app-type-description">{type.description}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="mobile-apps__benefits">
        <div className="mobile-apps__section-header">
          <motion.h2
            className="mobile-apps__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Why Choose Our Mobile App Development
          </motion.h2>
        </div>
        <div className="mobile-apps__benefits-grid">
          {benefits.map((benefit, index) => {
            const BenefitIcon = benefit.icon;
            return (
              <motion.div
                key={benefit.title}
                className="mobile-apps__benefit-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="mobile-apps__benefit-icon">
                  <BenefitIcon />
                </div>
                <h3 className="mobile-apps__benefit-title">{benefit.title}</h3>
                <p className="mobile-apps__benefit-description">{benefit.description}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Completed Projects Section */}
      <section className="mobile-apps__process">
        <div className="mobile-apps__section-header">
          <motion.h2
            className="mobile-apps__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Completed Latest Projects
          </motion.h2>
          <motion.p
            className="mobile-apps__section-subtitle"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            Real mobile applications we've built for real clients
          </motion.p>
        </div>
        <div 
          className="mobile-apps__projects-container"
          style={{
            display: 'flex',
            flexDirection: 'row' as const,
            flexWrap: 'nowrap' as const,
            gap: '30px',
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '0 20px'
          }}
        >
          {completedProjects.map((project, index) => (
            <motion.div
              key={project.title}
              className="mobile-apps__project-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -10 }}
              style={{
                flex: '1 1 calc(33.333% - 20px)',
                minWidth: '300px',
                maxWidth: 'calc(33.333% - 20px)',
                background: 'white',
                borderRadius: '15px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
              }}
            >
              <div 
                className="mobile-apps__project-image"
                style={{
                  position: 'relative',
                  height: '250px',
                  overflow: 'hidden'
                }}
              >
                <img 
                  src={project.image} 
                  alt={project.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
                <div 
                  className="mobile-apps__project-overlay"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(to top, rgba(103, 58, 183, 0.9), transparent)',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-end',
                    padding: '20px',
                    gap: '8px'
                  }}
                >
                  {project.technologies.map((tech) => {
                    const TechIcon = tech.icon;
                    return (
                      <span
                        key={tech.name}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          padding: '5px 12px',
                          background: 'rgba(255,255,255,0.95)',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600',
                          color: tech.color
                        }}
                      >
                        <TechIcon /> {tech.name}
                      </span>
                    );
                  })}
                </div>
              </div>
              <div 
                className="mobile-apps__project-content"
                style={{
                  padding: '25px'
                }}
              >
                <h3 
                  style={{
                    fontSize: '22px',
                    fontWeight: '700',
                    marginBottom: '12px',
                    color: '#2c3e50'
                  }}
                >
                  {project.title}
                </h3>
                <p 
                  style={{
                    fontSize: '15px',
                    lineHeight: '1.6',
                    color: '#555',
                    marginBottom: '20px'
                  }}
                >
                  {project.description}
                </p>
                <div 
                  className="mobile-apps__project-metrics"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    paddingTop: '20px',
                    borderTop: '1px solid #eee'
                  }}
                >
                  {Object.entries(project.metrics).map(([key, value]) => (
                    <div 
                      key={key}
                      style={{
                        textAlign: 'center'
                      }}
                    >
                      <div 
                        style={{
                          fontSize: '18px',
                          fontWeight: '700',
                          color: '#673ab7',
                          marginBottom: '5px'
                        }}
                      >
                        {value}
                      </div>
                      <div 
                        style={{
                          fontSize: '12px',
                          color: '#888',
                          textTransform: 'capitalize'
                        }}
                      >
                        {key}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="mobile-apps__testimonials">
        <div className="mobile-apps__section-header">
          <motion.h2
            className="mobile-apps__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Client Success Stories
          </motion.h2>
        </div>
        <div className="mobile-apps__testimonials-grid">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.author}
              className="mobile-apps__testimonial-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="mobile-apps__testimonial-quote">
                <FaQuoteLeft />
              </div>
              <div className="mobile-apps__testimonial-stars">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <FaStar key={i} />
                ))}
              </div>
              <p className="mobile-apps__testimonial-text">"{testimonial.text}"</p>
              <div className="mobile-apps__testimonial-author">
                <strong>{testimonial.author}</strong>
                <span>{testimonial.company}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQ Section - SEO Boost */}
      <section className="mobile-apps__faq">
        <div className="container">
          <motion.div
            className="mobile-apps__section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Frequently Asked Questions About Mobile App Development</h2>
            <p>Everything you need to know about creating successful mobile applications</p>
          </motion.div>
          <div className="mobile-apps__faq-list">
            <motion.div
              className="mobile-apps__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h3>How much does it cost to develop a mobile app?</h3>
              <p>As compare to market price, App Developer Pro delivers high performance mobile applications at very low cost. Mobile app costs vary based on complexity and features. A basic app starts at $1,000-$2,000, mid-level apps range from $2,000-$3,000, and complex enterprise apps can cost $3,000-$5,000+. Factors include features, platforms (iOS/Android), design complexity, and backend infrastructure.</p>
            </motion.div>
            <motion.div
              className="mobile-apps__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3>Should I build for iOS, Android, or both?</h3>
              <p>This depends on your target audience and budget. iOS users typically have higher engagement and spending power, while Android has a larger global market share. App Developer Pro recommends starting with one platform to validate your concept, then expanding. Cross-platform frameworks like React Native can build for both simultaneously, reducing costs.</p>
            </motion.div>
            <motion.div
              className="mobile-apps__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3>How long does mobile app development take?</h3>
              <p>App Developer Pro is too fast from others, Where development timelines vary: Simple apps take 3-4 weeks, medium complexity apps take 4-6 weeks, and complex enterprise apps take 6-12+ weeks. This includes planning, design, development, testing, and deployment phases. We provide detailed timelines during project kickoff.</p>
            </motion.div>
            <motion.div
              className="mobile-apps__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h3>Do you help with App Store and Google Play submission?</h3>
              <p>Yes! App Developer Pro handles the entire submission process for both App Store and Google Play. This includes creating developer accounts, preparing app descriptions, screenshots, compliance documentation, and managing the review process until your app is live.</p>
            </motion.div>
            <motion.div
              className="mobile-apps__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <h3>What happens after my app is launched?</h3>
              <p>Post-launch, App Developer Pro provides ongoing support including bug fixes, performance monitoring, feature updates, OS compatibility updates, security patches, and analytics tracking. We offer maintenance packages starting at $80/month to ensure your app stays current and performs optimally.</p>
            </motion.div>
            <motion.div
              className="mobile-apps__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <h3>Can you integrate my app with existing systems?</h3>
              <p>Absolutely! App Developer Pro specializes in integrating mobile apps with existing systems including CRM, ERP, payment gateways, cloud services, databases, and third-party APIs. I highly ensure seamless data synchronization and secure communication between your app and backend systems.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="mobile-apps__cta">
        <motion.div
          className="mobile-apps__cta-content"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="mobile-apps__cta-title">Ready to Build Your Mobile App?</h2>
          <p className="mobile-apps__cta-text">
            Transform your idea into a successful mobile app. Contact us today for a free consultation and project estimate.
          </p>
          <div className="mobile-apps__cta-buttons">
            <Link to="/contact" className="mobile-apps__cta-btn mobile-apps__cta-btn--primary">
              Get Free Quote <FaArrowRight />
            </Link>
            <Link to="/portfolio" className="mobile-apps__cta-btn mobile-apps__cta-btn--secondary">
              View App Portfolio
            </Link>
          </div>
        </motion.div>
      </section>
      </div>
    </>
  );
};

export default MobileApps;

